import { useLayoutEffect, useRef, useState } from 'react'

import { useGSAP } from '@/composables/useGSAP'

export type RunnerGoalDictationItemProps = {
  index: number
  text: string
  collected: boolean
  searched: boolean
}

export default function RunnerGoalDictationItem({ index, text, collected, searched }: RunnerGoalDictationItemProps) {
  const { gsap } = useGSAP()

  const elementRef = useRef<HTMLSpanElement>(null)
  const arrowRef = useRef<SVGSVGElement>(null)
  const arrowBodyRef = useRef<SVGPathElement>(null)
  const arrowHeadRef = useRef<SVGPathElement>(null)
  const arrowBodyAltRef = useRef<SVGPathElement>(null)
  const arrowHeadAltRef = useRef<SVGPathElement>(null)

  const [previousSearched, setPreviousSearched] = useState<boolean>(false)

  const backgroundClassName = (() => {
    if (collected) {
      return 'bg-light-blue'
    } else if (searched) {
      return 'bg-mid-blue'
    } else {
      return 'bg-dark-cream'
    }
  })()

  const innerClassName = (() => {
    if (collected) {
      return 'text-cream'
    } else if (searched) {
      return 'text-cream'
    } else {
      return 'text-primary'
    }
  })()

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (searched) {
        gsap
          .timeline({ delay: 0.5 })
          .to(arrowRef.current, { opacity: 1, duration: 0.5, ease: 'power2.out' }, 0)
          .fromTo(
            [arrowBodyAltRef.current, arrowBodyRef.current],
            { drawSVG: '0% 0%' },
            { drawSVG: '0% 100%', duration: 1, ease: 'power2.out', stagger: 0.15 },
            0
          )
          .fromTo(
            [arrowHeadAltRef.current, arrowHeadRef.current],
            { drawSVG: '50% 50%' },
            { drawSVG: '0% 100%', duration: 0.3, ease: 'power2.out', stagger: 0.15 },
            0.7
          )
      } else {
        gsap
          .timeline()
          .to(arrowRef.current, { opacity: 0, duration: previousSearched ? 0.5 : 0, ease: 'power2.out' }, 0)
      }
      setPreviousSearched(searched)
    })

    return () => ctx.kill()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searched, arrowBodyRef, arrowHeadRef, gsap, setPreviousSearched])

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      if (collected) {
        gsap.timeline().fromTo(
          elementRef.current,
          {
            rotate: 0,
          },
          {
            rotate: index % 2 === 0 ? 2 : -2,
            duration: 0.7,
            ease: 'back.inOut(10)',
          },
          0
        )
      }
    })

    return () => ctx.kill()
  }, [index, collected, elementRef, gsap])

  return (
    <span ref={elementRef} className={`RunnerGoalDictationItem relative h-32 md:h-40 pt-4 md:pt-0 px-16 md:pb-8`}>
      <span
        className={`absolute top-0 left-0 w-full h-full rounded-16 transition-colors duration-500 ease-out-quad ${backgroundClassName}`}
      />
      <span className={`relative transition-colors duration-500 ease-out-quad ${innerClassName}`}>
        {collected ? (
          <>{text}</>
        ) : (
          <span className='Runner__TextDictation'>
            <span>{text.split('').map((_) => '_')}</span>
          </span>
        )}
      </span>

      <svg
        ref={arrowRef}
        className='absolute top-[75%] md:top-[85%] -left-16 w-64 h-64 md:w-80 md:h-80 text-primary'
        viewBox='0 0 80 80'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'
      >
        <path
          ref={arrowBodyAltRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowHeadAltRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
          className='opacity-30'
        />
        <path
          ref={arrowBodyRef}
          d='M20.1399 66.7258C66.9892 45.7391 41.195 19.7617 29.9175 28.0308C15.8207 38.3672 48.499 62.4067 64.5679 19.2146'
          stroke='currentColor'
          strokeWidth='2'
        />
        <path
          ref={arrowHeadRef}
          d='M66.5902 28.5113L64.8876 18.6602L55.43 22.7858'
          stroke='currentColor'
          strokeWidth='2'
        />
      </svg>
    </span>
  )
}
